/**
 * Shared reference modal — relationship types + prioritization failure patterns.
 */

import { AlertTriangle, Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { RELATIONSHIP_UI_GUIDE, RELATIONSHIP_TYPE_COUNT, relationshipGuideCardClass } from '../../../lib/relationshipGuide';
import {
  ANTIPATTERN_GROUPS,
  PRIORITIZATION_ANTIPATTERNS,
  antipatternCardClass,
  dispositionBadgeClass,
  dispositionLabel,
} from '../../../lib/prioritizationAntipatterns';

export type RelationshipReferenceTab = 'types' | 'patterns';

interface RelationshipReferenceModalProps {
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  initialTab?: RelationshipReferenceTab;
}

function AntipatternCard({ entry, isDark }: { entry: (typeof PRIORITIZATION_ANTIPATTERNS)[number]; isDark: boolean }) {
  return (
    <div className={antipatternCardClass(isDark, entry.group)}>
      <div className="flex items-start justify-between gap-2 flex-wrap mb-2">
        <div>
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className={`text-xs font-mono font-semibold ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
              {entry.code}
            </span>
            <span className={`font-semibold ${isDark ? 'text-[var(--text-pri)]' : 'text-[var(--text-pri)]'}`}>{entry.name}</span>
          </div>
          <p className={`text-xs italic mt-0.5 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>aka {entry.aka}</p>
        </div>
        <span className={dispositionBadgeClass(entry.disposition, isDark)}>{dispositionLabel(entry.disposition)}</span>
      </div>
      <dl className={`space-y-2 text-sm ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
        <div>
          <dt className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>Structure</dt>
          <dd>{entry.structure}</dd>
        </div>
        <div>
          <dt className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>The pain</dt>
          <dd>{entry.pain}</dd>
        </div>
        <div>
          <dt className={`text-[10px] font-semibold uppercase tracking-wide mb-0.5 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>Signal</dt>
          <dd>{entry.signal}</dd>
        </div>
      </dl>
      <p className={`text-[11px] mt-3 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
        <span className="font-medium">Canon:</span> {entry.canon}
        {' · '}
        <span className="font-medium">Status:</span> {entry.status}
      </p>
    </div>
  );
}

export function RelationshipReferenceModal({
  isOpen,
  onClose,
  isDark,
  initialTab = 'types',
}: RelationshipReferenceModalProps) {
  const [tab, setTab] = useState<RelationshipReferenceTab>(initialTab);

  useEffect(() => {
    if (isOpen) setTab(initialTab);
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  const shell = isDark ? 'bg-[var(--bg)] border border-[var(--rule)]' : 'bg-[var(--bg-raised)]';
  const border = isDark ? 'border-[var(--rule)]' : 'border-[var(--rule)]';
  const tabActive = isDark
    ? 'bg-[var(--bg-raised)] text-[var(--text-pri)] border-[var(--rule)]'
    : 'bg-[var(--bg-raised)] text-[var(--text-pri)] border-[var(--rule)] shadow-sm';
  const tabIdle = isDark
    ? 'text-[var(--text-ter)] hover:text-[var(--text-ter)] border-transparent'
    : 'text-[var(--text-ter)] hover:text-[var(--text-pri)] border-transparent';

  const patternsByGroup = ANTIPATTERN_GROUPS.map((group) => ({
    group,
    entries: PRIORITIZATION_ANTIPATTERNS.filter((e) => e.group === group.id),
  }));

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className={` shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col ${shell}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between px-6 py-4 border-b flex-shrink-0 ${border}`}>
          <div className="flex items-center gap-3 min-w-0">
            {tab === 'types' ? (
              <Info className="h-5 w-5 text-[var(--text-ter)] shrink-0" />
            ) : (
              <AlertTriangle className="h-5 w-5 text-[var(--mustard)] shrink-0" />
            )}
            <div className="min-w-0">
              <h2 className={`text-lg font-semibold ${isDark ? 'text-[var(--text-pri)]' : 'text-[var(--text-pri)]'}`}>
                {tab === 'types' ? 'Relationship types' : 'Failure patterns'}
              </h2>
              <p className={`text-sm truncate ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                {tab === 'types'
                  ? `When to use each of the ${RELATIONSHIP_TYPE_COUNT} relationship types`
                  : 'Structural mistakes the platform detects before ranking'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`p-2  shrink-0 ${isDark ? 'hover:bg-[var(--bg-raised)]' : 'hover:bg-[var(--hover-tint)]'}`}
          >
            <X className={`h-5 w-5 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`} />
          </button>
        </div>

        <div className={`flex gap-1 px-6 pt-3 pb-0 flex-shrink-0 border-b ${border}`}>
          {(
            [
              { id: 'types' as const, label: 'Types' },
              { id: 'patterns' as const, label: 'Failure patterns' },
            ] as const
          ).map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-3 py-2 text-sm font-medium  border-b-2 -mb-px transition-colors ${
                tab === t.id ? tabActive : tabIdle
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {tab === 'types' &&
            RELATIONSHIP_UI_GUIDE.map((rel) => (
              <div key={rel.id} className={relationshipGuideCardClass(isDark, rel)}>
                <div className="flex items-start gap-3">
                  <div className={`flex-shrink-0 mt-0.5 ${isDark ? rel.iconColorDark : rel.iconColor}`}>
                    <rel.Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 flex-wrap mb-1">
                      <span className={`font-semibold ${isDark ? 'text-[var(--text-pri)]' : 'text-[var(--text-pri)]'}`}>{rel.label}</span>
                      <span className={`text-xs ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                        {rel.axis} · {rel.bond}
                      </span>
                    </div>
                    <p className={`text-sm font-medium mb-2 italic ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
                      "{rel.tagline}"
                    </p>
                    <p className={`text-sm mb-3 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>{rel.description}</p>
                    <div className="space-y-1">
                      <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                        Examples
                      </p>
                      <ul className="space-y-1">
                        {rel.examples.map((ex, i) => (
                          <li key={i} className={`text-xs flex items-start gap-1.5 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
                            <span className="flex-shrink-0 mt-0.5 opacity-40">·</span>
                            {ex}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <p className={`text-xs mt-3 italic ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>{rel.whenNotTo}</p>
                  </div>
                </div>
              </div>
            ))}

          {tab === 'patterns' && (
            <>
              <p className={`text-sm ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-sec)]'}`}>
                Most prioritization failures are bad structure, not bad judgment. Patterns A–B are detected on pools and
                tracks today; C lives at the planning boundary; D surfaces via governance gates.
              </p>
              {patternsByGroup.map(({ group, entries }) => (
                <div key={group.id}>
                  <h3 className={`text-xs font-semibold uppercase tracking-wide mb-1 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                    {group.id}. {group.title}
                  </h3>
                  <p className={`text-xs mb-3 ${isDark ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>{group.description}</p>
                  <div className="space-y-3">
                    {entries.map((entry) => (
                      <AntipatternCard key={entry.id} entry={entry} isDark={isDark} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className={`flex justify-end px-6 py-3 border-t flex-shrink-0 ${border} ${isDark ? 'bg-[var(--bg-raised)]/50' : 'bg-[var(--surface)]'}`}>
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2 text-sm font-medium  ${
              isDark
                ? 'text-[var(--text-ter)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]'
                : 'text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]'
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
